import { t } from 'i18next';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import { useContext, useEffect, useState } from 'react';
import {
  Claim,
  ClaimError,
  DatalistOption,
  DrivingConviction,
  DrivingConvictionError,
  DrivingHistoryApiResponseModel,
  InsuranceJourneyContext,
  MedicalCondition,
  MedicalConditionError,
  SelectOpt
} from '../../common/CommonTypes';
import Tooltip from '@mui/material/Tooltip';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useNavigate, useOutletContext } from 'react-router-dom';
import RadioSelection from '../../common/Product/RadioSelection';
import { ButtonBack } from '../../common/Button/ButtonBack';
import {
  renderRadioOptions,
  setCountryCode,
  populateSelectOptions
} from '../../common/HelperMethods';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import Claims from '../../common/Claims/Claims';
import DrivingConvictions from '../../common/DrivingConvictions/DrivingConvictions';
import MedicalConditions from '../../common/MedicalConditions/MedicalConditions';
import {
  InsuranceJourneyHelperText,
  InsuranceSaveForLaterMessage,
  PageAbsolutePath
} from '../../../consts/Common';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { PagesWithDropdown } from '../../common/Constants';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import ViewDocument from '../../common/ViewDocument/ViewDocument';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

export type DrivingPageErrors = {
  drivingConvictions: DrivingConvictionError[];
  nonMotoringConvictions: string;
  declinedInsurance: string;
  licenceType: string;
  yearsLicenceHeld: string;
  noClaimsBonusYear: string;
  hadClaimAccidentLosses: string;
  hadDrivingConvictions: string;
  claims: ClaimError[];
  hadMedicalConditionOrDisabilities: string;
  medicalConditions: MedicalConditionError[];
  ncdDiscount: string;
};

const initialErrors: DrivingPageErrors = {
  drivingConvictions: [],
  nonMotoringConvictions: '',
  declinedInsurance: '',
  licenceType: '',
  yearsLicenceHeld: '',
  noClaimsBonusYear: '',
  hadClaimAccidentLosses: '',
  hadDrivingConvictions: '',
  claims: [],
  hadMedicalConditionOrDisabilities: '',
  medicalConditions: [],
  ncdDiscount: ''
};

const DrivingHistoryPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);

  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const [errors, setErrors] = useState<DrivingPageErrors>(initialErrors);

  const {
    personalDetails,
    drivingHistoryDetails,
    staticData,
    savingQuote,
    pageLoading
  } = journey;

  const { drivingHistoryDetailsPage, commonEvents } = pageEvents;
  const { onPageChange, setPageLoading } = commonEvents;

  const {
    onChangeLicenceType,
    onChangeYearsLicenceHeld,
    onChangeHadClaimAccidentLosses,
    onChangeHadMedicalConditionOrDisabilities,
    onChangeDeclinedInsurance,
    onChangeHadDrivingConvictions,
    onChangeNonMotoringConvictions,
    onChangeNoClaimBonusYears,
    onUpdateClaims,
    onUpdateDrivingConvictions,
    onUpdateMedicalConditions,
    onChangeNcdDiscount
  } = drivingHistoryDetailsPage;

  const { setSavingQuote, setStaticData } = commonEvents;

  const { dateOfBirth } = personalDetails;

  const {
    licenceType,
    yearsLicenceHeld,
    hadDeclinedInsurance,
    hasNonMotoringConvictions,
    noClaimsBonusYear,
    hadClaimAccidentLosses,
    hadDrivingConvictions,
    claims,
    drivingConvictions,
    hadMedicalConditionOrDisabilities,
    medicalConditions,
    ncdDiscount
  } = drivingHistoryDetails;

  const token = site.user.token;

  const [licenceTypeOptions, setLicenceTypeOptions] = useState<SelectOpt[]>([]);
  const [noClaimYearsOptions, setNoClaimYearsOptions] = useState<SelectOpt[]>(
    []
  );
  const [licenceHeldYearsOptions, setLicenceHeldYearsOptions] = useState<
    SelectOpt[]
  >([]);
  const [claimTypeOptions, setClaimTypeOptions] = useState<SelectOpt[]>([]);

  const [convictionTypeOptions, setConvictionTypeOptions] = useState<
    SelectOpt[]
  >([]);

  const [lengthOfBanTypeOptions, setLengthOfBanTypeOptions] = useState<
    SelectOpt[]
  >([]);

  const [medicalConditionTypeOptions, setMedicalConditionTypeOptions] =
    useState<DatalistOption[]>([]);

  const [showPopup, setShowPopup] = useState(false);

  const questionnaireOptions = ['Yes', 'No'];

  const { getDrivingHistoryPageDropdownValues } = useCarInsuranceController();

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const getYears = (str: string): number => {
    const yrs = parseInt(
      noClaimYearsOptions.find((x) => x.value === str)?.id ?? '0'
    );
    return yrs;
  };

  const showQuestionNcdDiscount = getYears(noClaimsBonusYear) >= 4;

  const setLicenceYearsHeldDropDownlistSuccess = (
    apiResponse: DrivingHistoryApiResponseModel
  ) => {
    const newValues = [
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'years-licence-held',
        values: populateSelectOptions(apiResponse.licenceHeldYears)
      }
    ];

    setStaticData(staticData ? staticData.concat(...newValues) : newValues);
  };

  const loadLicenceYearsHeldDropDownList = async () => {
    const controller = new AbortController();

    await new CarInsuranceApi().GetDropDownValuesForDrivingHistoryAsync(
      dateOfBirth,
      token,
      setLicenceYearsHeldDropDownlistSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const dropDownValuesForPage = staticData?.find(
      (d) => d.page === PagesWithDropdown.DrivingHistoryPage
    );

    if (!dropDownValuesForPage)
      getDrivingHistoryPageDropdownValues(
        dateOfBirth,
        token,
        getDrivingHistoryPageDropdownValuesSuccess,
        onBackendFailure
      );
    else {
      const index = staticData?.findIndex(
        (d) =>
          d.page === PagesWithDropdown.DrivingHistoryPage &&
          d.controlName === 'years-licence-held'
      );

      if (index < 0) {
        loadLicenceYearsHeldDropDownList();
      }
    }
  }, []);

  useEffect(() => {
    validateAndShowPopup(licenceType);
  }, [licenceType]);

  useEffect(() => {
    const yearsLicenceHeldValues = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'years-licence-held'
    )?.values;

    yearsLicenceHeldValues
      ? setLicenceHeldYearsOptions(yearsLicenceHeldValues)
      : setLicenceHeldYearsOptions([]);

    const licenceTypes = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'licence-types'
    )?.values;

    licenceTypes
      ? setLicenceTypeOptions(licenceTypes)
      : setLicenceTypeOptions([]);

    const noClaimYearOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'no-claim-years'
    )?.values;

    noClaimYearOptions
      ? setNoClaimYearsOptions(noClaimYearOptions)
      : setNoClaimYearsOptions([]);

    const claimTypeOptionsValues = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'claim-types'
    )?.values;

    claimTypeOptionsValues
      ? setClaimTypeOptions(claimTypeOptionsValues)
      : setClaimTypeOptions([]);

    const drivingConvictionOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'driving-conviction-types'
    )?.values;

    drivingConvictionOptions
      ? setConvictionTypeOptions(drivingConvictionOptions)
      : setConvictionTypeOptions([]);

    const drivingBanLengthOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'driving-ban-length-types'
    )?.values;

    drivingBanLengthOptions
      ? setLengthOfBanTypeOptions(drivingBanLengthOptions)
      : setLengthOfBanTypeOptions([]);

    const medicalConditionOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.DrivingHistoryPage &&
        d.controlName === 'medical-conditions'
    )?.values;

    medicalConditionOptions
      ? setMedicalConditionTypeOptions(medicalConditionOptions)
      : setMedicalConditionTypeOptions([]);
  }, [staticData]);

  const getDrivingHistoryPageDropdownValuesSuccess = (
    dropdownValues: DrivingHistoryApiResponseModel
  ) => {
    const newValues = [
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'years-licence-held',
        values: populateSelectOptions(dropdownValues.licenceHeldYears)
      },
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'licence-types',
        values: populateSelectOptions(dropdownValues.licenceTypes)
      },
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'no-claim-years',
        values: populateSelectOptions(dropdownValues.noClaimsBonusYears)
      },
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'claim-types',
        values: populateSelectOptions(dropdownValues.claimTypes)
      },
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'driving-conviction-types',
        values: populateSelectOptions(dropdownValues.drivingConvictionTypes)
      },
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'driving-ban-length-types',
        values: populateSelectOptions(dropdownValues.drivingBanLengths)
      },
      {
        page: PagesWithDropdown.DrivingHistoryPage,
        controlName: 'medical-conditions',
        values: populateSelectOptions(dropdownValues.medicalConditions)
      }
    ];

    setStaticData(staticData ? staticData.concat(...newValues) : newValues);
  };

  const validate = (): boolean => {
    let isValid = true;
    const error: DrivingPageErrors = {
      ...initialErrors
    };

    if (
      CommonValidation.isEmpty(licenceType) ||
      licenceType == SelectInputHelper.NullValue()
    ) {
      error.licenceType = 'Licence type is required';
      isValid = false;
    }

    if (
      CommonValidation.isEmpty(yearsLicenceHeld) ||
      yearsLicenceHeld == SelectInputHelper.NullValue()
    ) {
      error.yearsLicenceHeld = 'No of years is required';
      isValid = false;
    }

    if (hadDeclinedInsurance === null) {
      error.declinedInsurance = 'This field is required';
      isValid = false;
    }

    if (showQuestionNcdDiscount && ncdDiscount === null) {
      error.ncdDiscount = 'This field is required';
      isValid = false;
    }

    if (hasNonMotoringConvictions === null) {
      error.nonMotoringConvictions = 'This field is required';
      isValid = false;
    }

    if (noClaimsBonusYear == SelectInputHelper.NullValue()) {
      error.noClaimsBonusYear = 'This field is required';
      isValid = false;
    }
    if (hadClaimAccidentLosses === null) {
      error.hadClaimAccidentLosses = 'This field is required';
      isValid = false;
    }
    if (hadDrivingConvictions === null) {
      error.hadDrivingConvictions = 'This field is required';
      isValid = false;
    }

    if (hadClaimAccidentLosses === true) {
      error.claims = claims.map((v) => {
        const e: ClaimError = {};
        if (CommonValidation.isEmpty(v.claimDate))
          e.claimDate = 'Date is required';
        if (
          SelectInputHelper.isNullValue(v.claimType) ||
          SelectInputHelper.isEmptyValue(v.claimType)
        )
          e.claimType = 'Claim type is required';
        return e;
      });

      if (
        error.claims.filter(
          (e) =>
            CommonValidation.isNotEmpty(e.claimDate) ||
            CommonValidation.isNotEmpty(e.claimType)
        ).length > 0
      )
        isValid = false;
    }

    if (hadDrivingConvictions) {
      error.drivingConvictions = drivingConvictions.map((v) => {
        const e: DrivingConvictionError = {};

        if (CommonValidation.isEmpty(v.convictionDate))
          e.convictionDate = 'Date is required';

        if (
          SelectInputHelper.isNullValue(v.convictionType) ||
          SelectInputHelper.isEmptyValue(v.convictionType)
        )
          e.convictionTypes = 'Claim type is required';

        if (
          SelectInputHelper.isNullValue(v.lengthOfBan) ||
          SelectInputHelper.isEmptyValue(v.lengthOfBan)
        )
          e.lengthOfBan = 'Field is required';

        if (v.points === 0 || v.points === undefined)
          e.points = 'Field is required';

        return e;
      });

      if (
        error.drivingConvictions.filter(
          (e) =>
            CommonValidation.isNotEmpty(e.convictionDate) ||
            CommonValidation.isNotEmpty(e.convictionTypes) ||
            CommonValidation.isNotEmpty(e.lengthOfBan) ||
            CommonValidation.isNotEmpty(e.points)
        ).length > 0
      )
        isValid = false;
    }

    if (
      hadMedicalConditionOrDisabilities === undefined ||
      hadMedicalConditionOrDisabilities === null
    ) {
      error.hadMedicalConditionOrDisabilities = 'This field is required';
      isValid = false;
    }

    if (hadMedicalConditionOrDisabilities === true) {
      if (medicalConditions.length === 0) {
        error.medicalConditions = [
          {
            canKeepLicence: 'Field is required',
            hasDVLANotified: 'Field is required',
            typeOfCondition: 'Field is required'
          }
        ];
        isValid = false;
      } else {
        error.medicalConditions = medicalConditions.map((v) => {
          const e: MedicalConditionError = {};

          if (CommonValidation.isEmpty(v.canKeepLicence))
            e.canKeepLicence = 'Field is required';

          if (CommonValidation.isEmpty(v.hasDVLANotified))
            e.hasDVLANotified = 'Field is required';

          if (CommonValidation.isEmpty(v.typeOfConditionKey))
            e.typeOfCondition = 'Field is required';

          return e;
        });

        if (
          error.medicalConditions.filter(
            (e) =>
              CommonValidation.isNotEmpty(e.canKeepLicence) ||
              CommonValidation.isNotEmpty(e.hasDVLANotified) ||
              CommonValidation.isNotEmpty(e.typeOfCondition)
          ).length > 0
        )
          isValid = false;
      }
    }

    setErrors(error);
    return isValid;
  };

  const onNextClick = () => {
    if (validate()) {
      const path: string = setCountryCode(
        PageAbsolutePath.Insurance.VehicleDetails,
        site.countryCode
      );
      navigate(path);
    }
    onPageChange(PageAbsolutePath.Insurance.DrivingHistory, true);
  };

  const onBackClick = () => {
    const path: string = setCountryCode(
      PageAbsolutePath.Insurance.AboutYou,
      site.countryCode
    );
    navigate(path);
  };

  const validateAndShowPopup = (value: string) => {
    value && value.includes('AUTO') && setShowPopup(true);
  };

  const onSaveLaterClick = (): void => {
    setPageLoading(true);
    setSavingQuote(true);
  };

  return (
    <LoadingContainer
      loading={pageLoading}
      message={InsuranceSaveForLaterMessage}>
      <>
        <div>
          <div className='one-col'>
            <ButtonCommand
              name='btn-back-top'
              text={'< Back'}
              onClick={onBackClick}
            />
          </div>
          <h4 className='section-header m3-top'>Driving History</h4>
          <div className='flex-row'>
            <div className='inline-control'>
              <div className='control-label'>Licence Type</div>
              <div className='control'>
                <SelectInput
                  name='licence-types'
                  className='input-text w250'
                  value={licenceType}
                  onChange={onChangeLicenceType}
                  options={licenceTypeOptions}
                  defaultText={t('personal-details.title-default')}
                  forceDefault
                  addDefaultOption
                  loadingText={t('loading-placeholder.loading-text')}
                  error={errors.licenceType}
                />
              </div>
            </div>
            <div className='inline-control'>
              <div className='control-label'>Years licence held</div>
              <div className='control'>
                <Tooltip
                  title={
                    'Please select Date of birth to calculate licence years'
                  }
                  enterTouchDelay={0}
                  arrow>
                  <SelectInput
                    name='years-licence-held'
                    className='input-text w250'
                    value={yearsLicenceHeld}
                    onChange={onChangeYearsLicenceHeld}
                    options={licenceHeldYearsOptions}
                    defaultText={t('personal-details.title-default')}
                    forceDefault
                    addDefaultOption
                    loadingText={t('loading-placeholder.loading-text')}
                    error={errors.yearsLicenceHeld}
                  />
                </Tooltip>
              </div>
            </div>
          </div>

          <div className='inline-control'>
            <RadioSelection
              show={true}
              selectionName={
                'Have you had any claims/accidents/losses in the last 5 years?'
              }
              fontDisabledClass=''
              error={errors.hadClaimAccidentLosses}>
              {renderRadioOptions(
                questionnaireOptions,
                'hadClaimAccidentLosses',
                hadClaimAccidentLosses === null
                  ? ''
                  : hadClaimAccidentLosses
                    ? 'Yes'
                    : 'No',
                (value) => onChangeHadClaimAccidentLosses(value === 'Yes')
              )}
            </RadioSelection>
          </div>
          {hadClaimAccidentLosses === true && (
            <Claims
              claims={
                claims?.length > 0
                  ? claims
                  : [{ claimType: '', claimDate: undefined } as Claim]
              }
              claimTypes={claimTypeOptions}
              errors={errors.claims}
              onUpdateClaims={onUpdateClaims}
            />
          )}

          <div className='inline-control'>
            <div className='control-label help-text'>
              {
                'Have you had any medical conditions or disabilities that are DVLA reportable?'
              }
              <ClickableHelpText
                helpText={
                  InsuranceJourneyHelperText.Pages.DrivingHistory
                    .Medicalconditions
                }
              />
            </div>
            <RadioSelection
              show={true}
              selectionName=''
              fontDisabledClass=''
              error={errors.hadMedicalConditionOrDisabilities}>
              {renderRadioOptions(
                questionnaireOptions,
                'hadMedicalConditionOrDisabilities',
                hadMedicalConditionOrDisabilities === null
                  ? ''
                  : hadMedicalConditionOrDisabilities
                    ? 'Yes'
                    : 'No',
                (value) =>
                  onChangeHadMedicalConditionOrDisabilities(value === 'Yes')
              )}
            </RadioSelection>
          </div>
          {hadMedicalConditionOrDisabilities && (
            <MedicalConditions
              medicalConditionTypes={medicalConditionTypeOptions}
              errorMessages={errors.medicalConditions}
              medicalConditions={
                medicalConditions?.length > 0
                  ? medicalConditions
                  : [
                      {
                        typeOfCondition: '',
                        hasDVLANotified: '',
                        canKeepLicence: ''
                      } as MedicalCondition
                    ]
              }
              onUpdateMedicalConditions={onUpdateMedicalConditions}
              brand={site.brand.brandName}
            />
          )}

          <div className='inline-control'>
            <RadioOptions
              errorMessage={errors.hadDrivingConvictions}
              label='Have you had any driving convictions, including fixed penalty notices, in the last five years?'
              name='driving-convictions'
              optionsList={questionnaireOptions}
              selectedOption={
                hadDrivingConvictions === null
                  ? ''
                  : hadDrivingConvictions
                    ? 'Yes'
                    : 'No'
              }
              setSelectedOption={(value) =>
                onChangeHadDrivingConvictions(value === 'Yes')
              }
            />
          </div>
          {hadDrivingConvictions && (
            <DrivingConvictions
              convictionTypes={convictionTypeOptions}
              lengthOfBanTypes={lengthOfBanTypeOptions}
              drivingConvictions={
                drivingConvictions?.length > 0
                  ? drivingConvictions
                  : [
                      {
                        convictionType: '',
                        convictionDate: undefined,
                        lengthOfBan: '',
                        points: 0
                      } as DrivingConviction
                    ]
              }
              errorMessages={errors.drivingConvictions}
              onUpdateDrivingConvictions={onUpdateDrivingConvictions}
            />
          )}
          <div className='inline-control'>
            <div className='control-label help-text'>
              {'Do you have any non motoring convictions?'}

              <ClickableHelpText
                helpText={
                  InsuranceJourneyHelperText.Pages.DrivingHistory
                    .NonMotoringConvictions
                }
              />
            </div>
            <RadioOptions
              errorMessage={errors.nonMotoringConvictions}
              label=''
              name='non-motoring-convictions'
              optionsList={questionnaireOptions}
              selectedOption={
                hasNonMotoringConvictions === null
                  ? ''
                  : hasNonMotoringConvictions
                    ? 'Yes'
                    : 'No'
              }
              setSelectedOption={(value) =>
                onChangeNonMotoringConvictions(value === 'Yes')
              }
            />
          </div>
          <div className='inline-control'>
            <div className='control-label help-text'>
              {
                'Have you ever had an insurance policy declined, cancelled, voided or had special terms imposed?'
              }
              <ClickableHelpText
                helpText={
                  InsuranceJourneyHelperText.Pages.DrivingHistory
                    .InsuranceDeclined
                }
              />
            </div>
            <RadioOptions
              errorMessage={errors.declinedInsurance}
              label=''
              name='declined-insurance'
              optionsList={questionnaireOptions}
              selectedOption={
                hadDeclinedInsurance === null
                  ? ''
                  : hadDeclinedInsurance
                    ? 'Yes'
                    : 'No'
              }
              setSelectedOption={(value) =>
                onChangeDeclinedInsurance(value === 'Yes')
              }
            />
          </div>
          <div className='inline-control'>
            <div className='control-label help-text'>
              {
                'How many no claims years have you earned in the UK on a private car in your name?'
              }
              <ClickableHelpText
                helpText={
                  <>
                    NCD can only be used on one policy at a time. You will need
                    to send us acceptable proof of your NCD within 21 days of
                    your policy start date. Please see our
                    <ViewDocument
                      path={`docs/Guide to NCD.pdf`}
                      documentName='Guide to NCD'
                    />
                    which outlines our NCD proof requirements.
                  </>
                }
              />
            </div>
            <div className='control'>
              <SelectInput
                name='noclaim-years'
                className='input-text w250'
                value={noClaimsBonusYear}
                onChange={onChangeNoClaimBonusYears}
                options={noClaimYearsOptions}
                defaultText={t('personal-details.title-default')}
                forceDefault
                addDefaultOption
                loadingText={t('loading-placeholder.loading-text')}
                error={errors.noClaimsBonusYear}
              />
            </div>
          </div>
          {showQuestionNcdDiscount && (
            <div className='inline-control'>
              <div className='control-label help-text'>
                {'Do you want to protect your No Claims Discount?'}
                <ClickableHelpText
                  helpText={
                    InsuranceJourneyHelperText.Pages.DrivingHistory
                      .ProtectNcdDiscount
                  }
                />
              </div>
              <RadioOptions
                errorMessage={errors.ncdDiscount}
                label=''
                name='ncd-discount'
                optionsList={questionnaireOptions}
                selectedOption={
                  ncdDiscount === null ? '' : ncdDiscount ? 'Yes' : 'No'
                }
                setSelectedOption={(value) =>
                  onChangeNcdDiscount(value === 'Yes')
                }
              />
            </div>
          )}
          <div className='even-space-out flex-row form-nav'>
            <ButtonBack
              name='btn-back'
              text='Back'
              onClick={onBackClick}
              additionalClassNames='btn-back'
            />
            <ButtonContinue
              name='btn-save-later'
              text='Save For Later'
              onClick={onSaveLaterClick}
              additionalClassNames='btn-save'
              disabled={savingQuote}
            />
            <ButtonContinue
              name='btn-next'
              text='Next'
              onClick={onNextClick}
              additionalClassNames='btn-continue'
            />
          </div>
        </div>
        <Modal
          role='modal-popup'
          isOpen={showPopup}
          className={`insurance-${site.brand.brandName}`}
          toggle={() => setShowPopup(false)}>
          <ModalHeader toggle={() => setShowPopup(false)}>
            We're Sorry
          </ModalHeader>
          <ModalBody>
            We cannot guarantee an automatic car under our standard courtesy car
            cover
          </ModalBody>
          <ModalFooter>
            <ButtonContinue
              name='modal-btn-continue'
              text={t('customer-product-page.button-quote-expired-continue')}
              onClick={() => setShowPopup(false)}
              additionalClassNames='btn-ok'
            />
          </ModalFooter>
        </Modal>
      </>
    </LoadingContainer>
  );
};

export default DrivingHistoryPage;
